import { Component, OnInit, ViewChild, ElementRef, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SvgIconComponent } from 'angular-svg-icon';
import { WeeklyHoursService, WeeklyHoursReportDetail, WeeklyHoursReportLine } from '../../../services/weekly-hours.service';
import { MissionDetailsComponent } from '../agency-etab-missions-details/encour-component/mission-details/mission-details.component';
import { FlagService } from '../../../shared/services/flag.service';
import { FlagType } from '../../../shared/utils/enums';

@Component({
  selector: 'app-weekly-hours-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, SvgIconComponent],
  templateUrl: './weekly-hours-detail.component.html',
  styleUrl: './weekly-hours-detail.component.scss'
})
export class WeeklyHoursDetailComponent implements OnInit, OnDestroy {
  report: WeeklyHoursReportDetail | null = null;

  /** Correction client (P5) : un relevé par fonction — le toggle Médecins / Infirmiers ne sert plus
   *  qu'aux anciens relevés combinés (typeSecteurId null). */
  pdfFonction: 'medecins' | 'infirmiers' = 'medecins';

  isLegacyCombined(): boolean {
    return !!this.report && (this.report.typeSecteurId === null || this.report.typeSecteurId === undefined);
  }

  // ── Custom horizontal scrollbar state (see .hscroll-track / .hscroll-thumb) ──
  hasHorizontalOverflow = false;
  thumbWidth = 0;   // px
  thumbLeft = 0;    // px, offset inside the track
  private readonly MIN_THUMB_PX = 40;
  private dragging = false;
  private dragStartX = 0;
  private dragStartScrollLeft = 0;

  private resizeObserver: ResizeObserver | null = null;
  private _tableContainer!: ElementRef<HTMLDivElement>;

  @ViewChild('hScrollTrack') hScrollTrack!: ElementRef<HTMLDivElement>;

  @ViewChild('tableContainer') set tableContainer(content: ElementRef<HTMLDivElement> | undefined) {
    if (content) {
      this._tableContainer = content;
      this.setupResizeObserver();
    } else {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    }
  }

  get tableContainer(): ElementRef<HTMLDivElement> {
    return this._tableContainer;
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined' && this._tableContainer) {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => this.updateScrollbar());
      });
      this.resizeObserver.observe(this._tableContainer.nativeElement);
    }
  }

  /** Recompute thumb size + position from the table's scroll geometry. */
  private updateScrollbar(): void {
    const table = this._tableContainer?.nativeElement;
    const track = this.hScrollTrack?.nativeElement;
    if (!table || !track) return;

    const clientW = table.clientWidth;
    const scrollW = table.scrollWidth;
    const trackW = track.clientWidth;

    this.hasHorizontalOverflow = scrollW > clientW + 1;
    if (!this.hasHorizontalOverflow) {
      this.thumbWidth = 0;
      this.thumbLeft = 0;
      return;
    }

    this.thumbWidth = Math.max(this.MIN_THUMB_PX, Math.round((trackW * clientW) / scrollW));
    const maxScroll = scrollW - clientW;
    const maxThumb = trackW - this.thumbWidth;
    this.thumbLeft = maxScroll > 0 ? Math.round((table.scrollLeft / maxScroll) * maxThumb) : 0;
  }

  /** Table scrolled (wheel / trackpad) → reposition the thumb. */
  onTableScroll(): void {
    this.updateScrollbar();
  }

  /** Start dragging the thumb. */
  onThumbPointerDown(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation(); // don't let the track's jump handler fire too
    if (!this._tableContainer) return;
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this._tableContainer.nativeElement.scrollLeft;
  }

  /** Click on the track (not the thumb) → jump the view so the thumb centers under the cursor. */
  onTrackPointerDown(event: PointerEvent): void {
    const table = this._tableContainer?.nativeElement;
    const track = this.hScrollTrack?.nativeElement;
    if (!table || !track || !this.hasHorizontalOverflow) return;

    const rect = track.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const maxThumb = track.clientWidth - this.thumbWidth;
    const maxScroll = table.scrollWidth - table.clientWidth;
    if (maxThumb <= 0) return;

    const targetThumbLeft = clickX - this.thumbWidth / 2;
    const ratio = Math.min(1, Math.max(0, targetThumbLeft / maxThumb));
    table.scrollLeft = ratio * maxScroll; // triggers onTableScroll → thumb follows
  }

  @HostListener('window:pointermove', ['$event'])
  onWindowPointerMove(event: PointerEvent): void {
    if (!this.dragging || !this._tableContainer || !this.hScrollTrack) return;
    const table = this._tableContainer.nativeElement;
    const maxThumb = this.hScrollTrack.nativeElement.clientWidth - this.thumbWidth;
    const maxScroll = table.scrollWidth - table.clientWidth;
    if (maxThumb <= 0) return;

    const dx = event.clientX - this.dragStartX;
    table.scrollLeft = this.dragStartScrollLeft + (dx / maxThumb) * maxScroll;
  }

  @HostListener('window:pointerup')
  onWindowPointerUp(): void {
    this.dragging = false;
  }
  loading = false;
  loadingPdf = false;
  pdfSrc: SafeResourceUrl | null = null;
  sending = false;
  regenerating = false;
  /** Per-line save state, keyed by line id */
  savingLineIds = new Set<number>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: WeeklyHoursService,
    private flag: FlagService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.router.navigate(['/agency/weekly-hours']);
      return;
    }
    this.load(id);
  }

  load(id: number): void {
    this.loading = true;
    this.service.get(id).subscribe({
      next: (data) => {
        this.report = data;
        this.loading = false;
        this.refreshPdf();
      },
      error: (err) => {
        this.loading = false;
        if (this.handleReportGone(err)) return;
        this.flag.displayFlag('Erreur lors du chargement du relevé', FlagType.error);
      }
    });
  }

  /** 410 : relevé retiré car plus aucune affectation à rapporter (missions supprimées / pros désaffectés). */
  private handleReportGone(err: any): boolean {
    if (err?.status !== 410) return false;
    this.flag.displayFlag(err?.error?.message || 'Ce relevé n\'a plus aucune ligne : il a été retiré.', FlagType.warning);
    this.router.navigate(['/agency/weekly-hours']);
    return true;
  }

  setPdfFonction(f: 'medecins' | 'infirmiers'): void {
    if (this.pdfFonction === f) return;
    this.pdfFonction = f;
    this.refreshPdf();
  }

  refreshPdf(): void {
    if (!this.report) return;
    this.loadingPdf = true;
    this.service.pdf(this.report.id, this.isLegacyCombined() ? this.pdfFonction : undefined).subscribe({
      next: (res) => {
        if (res?.contractBytes) {
          const url = this.base64ToBlobUrl(res.contractBytes);
          this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        }
        this.loadingPdf = false;
      },
      error: () => {
        this.loadingPdf = false;
      }
    });
  }

  /** Save a single line — called on input blur for each editable cell. */
  saveLine(line: WeeklyHoursReportLine): void {
    if (!this.report || this.isLocked()) return;
    this.savingLineIds.add(line.id);
    this.service.updateLine(this.report.id, line.id, {
      startDate: line.startDate,
      endDate: line.endDate,
      startTime: line.startTime,
      endTime: line.endTime,
      pauseMinutes: line.pauseMinutes,
      observations: line.observations,
      lineState: line.lineState
    }).subscribe({
      next: (updated) => {
        // Mirror the recomputed totalHours back into the row
        line.totalHours = updated.totalHours;
        line.edited = true;
        this.savingLineIds.delete(line.id);
        this.refreshPdf();
      },
      error: () => {
        this.flag.displayFlag('Erreur lors de la sauvegarde', FlagType.error);
        this.savingLineIds.delete(line.id);
      }
    });
  }

  regenerate(): void {
    if (!this.report) return;
    if (!confirm('Régénérer ce relevé ? Les lignes que vous avez modifiées seront conservées.')) return;
    this.regenerating = true;
    this.service.regenerate(this.report.id).subscribe({
      next: (data) => {
        this.report = data;
        this.regenerating = false;
        this.refreshPdf();
        this.flag.displayFlag('Relevé régénéré', FlagType.success);
      },
      error: () => {
        this.flag.displayFlag('Erreur lors de la régénération', FlagType.error);
        this.regenerating = false;
      }
    });
  }

  send(): void {
    if (!this.report || !this.report.canSendNow) return;
    this.sending = true;
    this.service.send(this.report.id).subscribe({
      next: () => {
        this.flag.displayFlag('Relevé envoyé à l\'établissement', FlagType.success);
        this.sending = false;
        this.load(this.report!.id);
      },
      error: (err) => {
        this.sending = false;
        if (this.handleReportGone(err)) return;
        this.flag.displayFlag('Erreur lors de l\'envoi', FlagType.error);
      }
    });
  }

  back(): void {
    this.router.navigate(['/agency/weekly-hours']);
  }

  /** Open the mission details dialog (read-only side panel) for this line's mission. */
  openMissionDetails(line: WeeklyHoursReportLine): void {
    if (!line.missionId) return;
    this.dialog.open(MissionDetailsComponent, {
      width: '666px',
      height: '100vh',
      position: { right: '0' },
      backdropClass: 'bg-transparent',
      data: { id: line.missionId, etbId: this.report?.etablissementId ?? 0 }
    });
  }

  isLocked(): boolean {
    return this.report?.status === 'SENT';
  }

  toggleCancelLine(line: WeeklyHoursReportLine): void {
    if (this.isLocked()) return;
    line.lineState = line.lineState === 'ANNULE' ? 'ACHEVE' : 'ANNULE';
    this.saveLine(line);
  }

  totalHoursDisplay(): string {
    if (!this.report) return '0.00';
    const total = this.report.lines
      .filter(l => l.lineState !== 'ANNULE')
      .reduce((acc, l) => acc + (Number(l.totalHours) || 0), 0);
    return total.toFixed(2);
  }

  isSaving(line: WeeklyHoursReportLine): boolean {
    return this.savingLineIds.has(line.id);
  }

  private base64ToBlobUrl(base64: string): string {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  }
}
